import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Badge,
  Box,
  Button,
  Callout,
  Card,
  Code,
  Dialog,
  Flex,
  Heading,
  IconButton,
  Select,
  Separator,
  Table,
  Text,
  TextField,
} from "@radix-ui/themes";
import { Check, Copy, Info, Image as ImageIcon, Plus, RefreshCw, Star, Trash2, Upload } from "lucide-react";
import { fileService } from "@/services/breeze/FileService";
import toast from "react-hot-toast";
import {
  TenantDomain,
  TenantSenderAddress,
  tenantDomainService,
} from "@/services/breeze/TenantDomainService";
import { isValidSendingHostname } from "@/utils/sendingDomain";

interface SendingDomainsPanelProps {
  showAdd: boolean;
  onShowAddChange: (next: boolean) => void;
}

export default function SendingDomainsPanel({ showAdd, onShowAddChange }: SendingDomainsPanelProps) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["tenant-domains"],
    queryFn: () => tenantDomainService.list(),
  });

  const [newHostname, setNewHostname] = useState("");
  const [showAddSender, setShowAddSender] = useState(false);

  const verifiedHostnames = useMemo(
    () => (data?.domains || []).filter((d) => d.is_verified).map((d) => d.hostname),
    [data?.domains],
  );
  const senders = data?.sender_addresses ?? [];

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["tenant-domains"] });

  const registerMutation = useMutation({
    mutationFn: (hostname: string) => tenantDomainService.register(hostname),
    onSuccess: () => {
      invalidate();
      setNewHostname("");
      onShowAddChange(false);
      toast.success("Domain registered. Set the DNS records, then click Verify.");
    },
  });

  const verifyMutation = useMutation({
    mutationFn: (id: number) => tenantDomainService.verify(id),
    onSuccess: (d) => {
      invalidate();
      toast.success(
        d.is_verified
          ? `${d.hostname} verified.`
          : `${d.hostname} not yet verified — DNS may still be propagating.`,
      );
    },
  });

  const deleteDomainMutation = useMutation({
    mutationFn: (id: number) => tenantDomainService.remove(id),
    onSuccess: () => {
      invalidate();
      toast.success("Domain removed.");
    },
  });

  const createSenderMutation = useMutation({
    mutationFn: (vars: { address: string; displayName: string | null; makeDefault: boolean }) =>
      tenantDomainService.createSender(vars.address, vars.displayName, vars.makeDefault),
    onSuccess: () => {
      invalidate();
      setShowAddSender(false);
      toast.success("Sender address added.");
    },
  });

  const updateSenderMutation = useMutation({
    mutationFn: (vars: { id: number; displayName: string | null }) =>
      tenantDomainService.updateSender(vars.id, vars.displayName),
    onSuccess: () => {
      invalidate();
      toast.success("Sender address updated.");
    },
  });

  const makeDefaultMutation = useMutation({
    mutationFn: (id: number) => tenantDomainService.makeSenderDefault(id),
    onSuccess: () => {
      invalidate();
      toast.success("Default sender updated.");
    },
  });

  const removeSenderMutation = useMutation({
    mutationFn: (id: number) => tenantDomainService.removeSender(id),
    onSuccess: () => {
      invalidate();
      toast.success("Sender address removed.");
    },
  });

  const setAvatarMutation = useMutation({
    mutationFn: async (vars: { domainId: number; file: File }) => {
      const fd = new FormData();
      fd.append("file", vars.file);
      const uploaded = await fileService.create(fd, true);
      return tenantDomainService.setAvatar(vars.domainId, uploaded.id);
    },
    onSuccess: () => {
      invalidate();
      toast.success("Sender avatar saved. Visible to Gmail recipients only.");
    },
  });

  const clearAvatarMutation = useMutation({
    mutationFn: (domainId: number) => tenantDomainService.clearAvatar(domainId),
    onSuccess: () => {
      invalidate();
      toast.success("Sender avatar cleared.");
    },
  });

  const canRegister = isValidSendingHostname(newHostname);

  return (
    <Box>
      <Box mb="4">
        <Heading size="4">Sending Domains</Heading>
        <Text size="2" color="gray">
          Authenticate the domains your project sends email from. Add the DNS
          records shown for each domain; once verified, workflows can send from
          any address on these domains.
        </Text>
      </Box>

      {isLoading ? (
        <Text size="2" color="gray">Loading…</Text>
      ) : (data?.domains.length ?? 0) === 0 ? (
        <Callout.Root>
          <Callout.Icon><Info size={16} /></Callout.Icon>
          <Callout.Text>
            No domains yet. Add the hostname you want to send from (e.g.
            <code style={{ marginInline: 4 }}>example.com</code>).
          </Callout.Text>
        </Callout.Root>
      ) : (
        <Flex direction="column" gap="3">
          {data?.domains.map((d) => (
            <DomainCard
              key={d.id}
              domain={d}
              onVerify={() => verifyMutation.mutate(d.id)}
              onDelete={() => {
                if (window.confirm(`Remove ${d.hostname}?`)) deleteDomainMutation.mutate(d.id);
              }}
              verifying={verifyMutation.isPending && verifyMutation.variables === d.id}
            />
          ))}
        </Flex>
      )}

      <Separator my="5" size="4" />

      <Box mb="3">
        <Box mb="2">
          <Flex align="center" gap="2">
            <Heading size="3">Sender avatars</Heading>
            <Badge color="blue" variant="soft">Gmail only</Badge>
          </Flex>
          <Text size="2" color="gray">
            The image Gmail recipients see next to messages from each domain.
            Other clients ignore this and show a generic avatar. Square
            PNG/JPG ~96×96 works best.
          </Text>
        </Box>

        {verifiedHostnames.length === 0 ? (
          <Callout.Root color="gray" mt="2">
            <Callout.Icon><Info size={16} /></Callout.Icon>
            <Callout.Text>
              Verify a domain first — avatars only apply to verified domains.
            </Callout.Text>
          </Callout.Root>
        ) : (
          <Flex direction="column" gap="2" mt="2">
            {data?.domains.filter((d) => d.is_verified).map((d) => (
              <AvatarRow
                key={d.id}
                domain={d}
                onUpload={(file) => setAvatarMutation.mutate({ domainId: d.id, file })}
                onClear={() => clearAvatarMutation.mutate(d.id)}
                busy={
                  (setAvatarMutation.isPending && setAvatarMutation.variables?.domainId === d.id) ||
                  (clearAvatarMutation.isPending && clearAvatarMutation.variables === d.id)
                }
              />
            ))}
          </Flex>
        )}
      </Box>

      <Separator my="5" size="4" />

      <Box>
        <Flex align="center" justify="between" mb="2">
          <Box>
            <Heading size="3">Sender addresses</Heading>
            <Text size="2" color="gray">
              Registered addresses workflows can send from. The default is used
              by Send-an-Email steps that don't specify a From address.
            </Text>
          </Box>
          <Button
            onClick={() => setShowAddSender(true)}
            disabled={verifiedHostnames.length === 0}
            title={
              verifiedHostnames.length === 0
                ? "Verify a domain first"
                : undefined
            }
          >
            <Plus size={14} /> Add sender
          </Button>
        </Flex>

        {senders.length === 0 ? (
          <Callout.Root mt="3" color="gray">
            <Callout.Icon><Info size={16} /></Callout.Icon>
            <Callout.Text>
              No sender addresses yet. Add one to use as the default for workflow
              Send-an-Email steps.
            </Callout.Text>
          </Callout.Root>
        ) : (
          <Table.Root variant="surface" mt="3" size="1">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>Display name</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Address</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell></Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell></Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {senders.map((s) => (
                <SenderRow
                  key={s.id}
                  sender={s}
                  onUpdate={(displayName) => updateSenderMutation.mutate({ id: s.id, displayName })}
                  onMakeDefault={() => makeDefaultMutation.mutate(s.id)}
                  onDelete={() => {
                    if (window.confirm(`Remove ${s.address}?`)) removeSenderMutation.mutate(s.id);
                  }}
                  updating={updateSenderMutation.isPending && updateSenderMutation.variables?.id === s.id}
                />
              ))}
            </Table.Body>
          </Table.Root>
        )}
      </Box>

      <Dialog.Root open={showAdd} onOpenChange={onShowAddChange}>
        <Dialog.Content style={{ maxWidth: 520 }}>
          <Dialog.Title>Add a sending domain</Dialog.Title>
          <Dialog.Description size="2" mb="3">
            Enter the bare hostname you want to send from. We'll issue DNS records
            you'll need to add at your DNS provider.
          </Dialog.Description>
          <TextField.Root
            placeholder="example.com"
            value={newHostname}
            onChange={(e) => setNewHostname(e.target.value)}
            autoFocus
          />
          {newHostname && !canRegister && (
            <Text size="1" color="red" mt="1">
              Not a valid hostname — try something like <code>mail.example.com</code>.
            </Text>
          )}
          <Flex gap="2" mt="4" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray">Cancel</Button>
            </Dialog.Close>
            <Button
              onClick={() => registerMutation.mutate(newHostname.trim().toLowerCase())}
              disabled={!canRegister || registerMutation.isPending}
            >
              {registerMutation.isPending ? "Registering…" : "Register"}
            </Button>
          </Flex>
        </Dialog.Content>
      </Dialog.Root>

      <AddSenderDialog
        open={showAddSender}
        onOpenChange={setShowAddSender}
        verifiedHostnames={verifiedHostnames}
        existingDefault={senders.some((s) => s.is_default)}
        onSubmit={(vars) => createSenderMutation.mutate(vars)}
        submitting={createSenderMutation.isPending}
      />
    </Box>
  );
}

function SenderRow({
  sender,
  updating,
  onUpdate,
  onMakeDefault,
  onDelete,
}: {
  sender: TenantSenderAddress;
  updating: boolean;
  onUpdate: (displayName: string | null) => void;
  onMakeDefault: () => void;
  onDelete: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(sender.display_name ?? "");

  return (
    <Table.Row>
      <Table.Cell>
        {editing ? (
          <Flex gap="2" align="center">
            <TextField.Root
              size="1"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              maxLength={80}
              autoFocus
              style={{ minWidth: 180 }}
            />
            <Button
              size="1"
              onClick={() => {
                onUpdate(draft.trim() === "" ? null : draft.trim());
                setEditing(false);
              }}
              disabled={updating}
            >
              Save
            </Button>
            <Button
              size="1"
              variant="soft"
              color="gray"
              onClick={() => {
                setDraft(sender.display_name ?? "");
                setEditing(false);
              }}
            >
              Cancel
            </Button>
          </Flex>
        ) : (
          <Flex gap="2" align="center">
            <Text size="2">
              {sender.display_name || <Text color="gray">—</Text>}
            </Text>
            <IconButton
              size="1"
              variant="ghost"
              color="gray"
              onClick={() => {
                setDraft(sender.display_name ?? "");
                setEditing(true);
              }}
              title="Edit display name"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 20h9M16.5 3.5a2.121 2.121 0 113 3L7 19l-4 1 1-4L16.5 3.5z" />
              </svg>
            </IconButton>
          </Flex>
        )}
      </Table.Cell>
      <Table.Cell>
        <Flex gap="2" align="center">
          <Code size="1" variant="ghost" style={{ wordBreak: "break-all" }}>{sender.address}</Code>
          {sender.is_default && (
            <Badge color="green" variant="soft">Default</Badge>
          )}
        </Flex>
      </Table.Cell>
      <Table.Cell style={{ width: 1, whiteSpace: "nowrap" }}>
        {!sender.is_default && (
          <Button
            size="1"
            variant="soft"
            onClick={onMakeDefault}
            title="Make this the default sender"
          >
            <Star size={12} /> Make default
          </Button>
        )}
      </Table.Cell>
      <Table.Cell style={{ width: 1 }}>
        <IconButton size="1" variant="soft" color="red" onClick={onDelete} title="Remove sender">
          <Trash2 size={12} />
        </IconButton>
      </Table.Cell>
    </Table.Row>
  );
}

function AddSenderDialog({
  open,
  onOpenChange,
  verifiedHostnames,
  existingDefault,
  onSubmit,
  submitting,
}: {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  verifiedHostnames: string[];
  existingDefault: boolean;
  onSubmit: (vars: { address: string; displayName: string | null; makeDefault: boolean }) => void;
  submitting: boolean;
}) {
  const [localPart, setLocalPart] = useState("noreply");
  const [host, setHost] = useState<string>(verifiedHostnames[0] ?? "");
  const [displayName, setDisplayName] = useState("");
  const [makeDefault, setMakeDefault] = useState(!existingDefault);

  useEffect(() => {
    if (host && !verifiedHostnames.includes(host) && verifiedHostnames.length > 0) {
      setHost(verifiedHostnames[0]);
    }
  }, [host, verifiedHostnames]);

  const localOk = /^[a-zA-Z0-9._%+\-]+$/.test(localPart.trim());
  const canSubmit = localOk && host && !submitting;

  const submit = () => {
    if (!canSubmit) return;
    onSubmit({
      address: `${localPart.trim().toLowerCase()}@${host}`,
      displayName: displayName.trim() === "" ? null : displayName.trim(),
      makeDefault,
    });
  };

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (next) {
          setLocalPart("noreply");
          setHost(verifiedHostnames[0] ?? "");
          setDisplayName("");
          setMakeDefault(!existingDefault);
        }
        onOpenChange(next);
      }}
    >
      <Dialog.Content style={{ maxWidth: 520 }}>
        <Dialog.Title>Add a sender address</Dialog.Title>
        <Dialog.Description size="2" mb="3">
          Register an address on one of your verified domains. The display name
          is what recipients see before the address.
        </Dialog.Description>

        <Flex direction="column" gap="3">
          <Box>
            <Text as="label" size="2" weight="medium" mb="1" style={{ display: "block" }}>
              Display name
            </Text>
            <TextField.Root
              placeholder="Getahead"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={80}
            />
          </Box>
          <Box>
            <Text as="label" size="2" weight="medium" mb="1" style={{ display: "block" }}>
              Address
            </Text>
            <Flex gap="2" align="center">
              <TextField.Root
                value={localPart}
                onChange={(e) => setLocalPart(e.target.value)}
                style={{ flex: 1 }}
              />
              <Text size="2" color="gray">@</Text>
              <Select.Root value={host} onValueChange={setHost}>
                <Select.Trigger style={{ minWidth: 200 }} />
                <Select.Content position="popper" style={{ zIndex: 11000 }}>
                  {verifiedHostnames.map((h) => (
                    <Select.Item key={h} value={h}>{h}</Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            </Flex>
          </Box>
          <Box>
            <label>
              <Flex gap="2" align="center">
                <input
                  type="checkbox"
                  checked={makeDefault}
                  onChange={(e) => setMakeDefault(e.target.checked)}
                />
                <Text size="2">
                  {existingDefault ? "Replace current default with this address" : "Make this the default sender"}
                </Text>
              </Flex>
            </label>
          </Box>
        </Flex>

        <Flex gap="2" mt="4" justify="end">
          <Dialog.Close>
            <Button variant="soft" color="gray">Cancel</Button>
          </Dialog.Close>
          <Button onClick={submit} disabled={!canSubmit}>
            {submitting ? "Adding…" : "Add sender"}
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}

function AvatarRow({
  domain,
  busy,
  onUpload,
  onClear,
}: {
  domain: TenantDomain;
  busy: boolean;
  onUpload: (file: File) => void;
  onClear: () => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  return (
    <Flex
      align="center"
      gap="3"
      p="2"
      style={{
        border: "1px solid var(--gray-5)",
        borderRadius: "var(--radius-2)",
        background: "var(--gray-2)",
      }}
    >
      <Box style={{
        width: 48, height: 48, borderRadius: "50%",
        background: "var(--gray-3)",
        border: "1px solid var(--gray-5)",
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0, overflow: "hidden",
      }}>
        {domain.avatar_url ? (
          <img
            src={domain.avatar_url}
            alt=""
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <ImageIcon size={18} style={{ color: "var(--gray-9)" }} />
        )}
      </Box>
      <Box style={{ flex: 1, minWidth: 0 }}>
        <Text size="2" weight="medium">{domain.hostname}</Text>
        <Text size="1" color="gray" style={{ display: "block" }}>
          {domain.avatar_url ? "Avatar set" : "No avatar"}
        </Text>
      </Box>
      <Flex gap="2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) onUpload(file);
            if (fileInputRef.current) fileInputRef.current.value = "";
          }}
        />
        <Button
          variant="soft"
          size="2"
          onClick={() => fileInputRef.current?.click()}
          disabled={busy}
        >
          <Upload size={14} /> {domain.avatar_url ? "Replace" : "Upload"}
        </Button>
        {domain.avatar_url && (
          <IconButton variant="soft" color="red" size="2" onClick={onClear} disabled={busy} title="Remove avatar">
            <Trash2 size={14} />
          </IconButton>
        )}
      </Flex>
    </Flex>
  );
}

function DomainCard({
  domain,
  verifying,
  onVerify,
  onDelete,
}: {
  domain: TenantDomain;
  verifying: boolean;
  onVerify: () => void;
  onDelete: () => void;
}) {
  return (
    <Card>
      <Flex align="center" justify="between" mb="3">
        <Flex align="center" gap="3">
          <Heading size="3">{domain.hostname}</Heading>
          {domain.is_verified ? (
            <Badge color="green" variant="soft">
              <Check size={12} /> Verified
            </Badge>
          ) : (
            <Badge color="amber" variant="soft">Pending DNS</Badge>
          )}
        </Flex>
        <Flex gap="2">
          <Button
            variant="soft"
            onClick={onVerify}
            disabled={verifying}
            title={domain.is_verified ? "Re-check DNS" : "Check DNS records"}
          >
            <RefreshCw size={14} /> {verifying ? "Checking…" : domain.is_verified ? "Re-check" : "Verify"}
          </Button>
          <IconButton variant="soft" color="red" onClick={onDelete} title="Remove domain">
            <Trash2 size={14} />
          </IconButton>
        </Flex>
      </Flex>

      {domain.dns_records.length === 0 ? (
        <Text size="2" color="gray">No DNS records returned.</Text>
      ) : (
        <Box>
          <Text size="2" weight="medium" mb="2">DNS records to add at your DNS provider:</Text>
          <Table.Root variant="surface" size="1">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeaderCell>Type</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Host</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>Value</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell>State</Table.ColumnHeaderCell>
                <Table.ColumnHeaderCell></Table.ColumnHeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {domain.dns_records.map((r, i) => (
                <Table.Row key={`${r.host}-${i}`}>
                  <Table.Cell><Code size="1" variant="ghost">{r.type}</Code></Table.Cell>
                  <Table.Cell><Code size="1" variant="ghost" style={{ wordBreak: "break-all" }}>{r.host}</Code></Table.Cell>
                  <Table.Cell><Code size="1" variant="ghost" style={{ wordBreak: "break-all" }}>{r.data}</Code></Table.Cell>
                  <Table.Cell>
                    {r.valid === true ? (
                      <Badge color="green" variant="soft">Valid</Badge>
                    ) : r.valid === false ? (
                      <Badge color="amber" variant="soft">Pending</Badge>
                    ) : (
                      <Badge color="gray" variant="soft">Unchecked</Badge>
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    <IconButton
                      variant="ghost"
                      size="1"
                      onClick={() => {
                        navigator.clipboard.writeText(r.data);
                        toast.success("Copied");
                      }}
                      title="Copy value"
                    >
                      <Copy size={14} />
                    </IconButton>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Box>
      )}
    </Card>
  );
}

// Error toasts are handled globally by BaseService.handleError
